import React, { useCallback, useMemo } from 'react';

import { useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router';

import { Alert, Box, Button, Card, CardContent, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';

import useQuery from '~/hooks/useQuery';

import { addQuery } from '~/utils/url';

import { brands } from '../../consts/brands';
import { CheckPromoDisplayParams, usePromoDisplayCheck } from '../../hooks/usePromoDisplayCheck';
import DebugModal from '../DebugModal/DebugModal';

export type PromoDisplayCheckFormData = {
  offerId: string;
  promoDisplayConfigId: string;
  brand: string;
  sessionId?: string;
};

type PromoDisplayCheckFormProps = {
  onSubmit?: (data: PromoDisplayCheckFormData) => void;
  isLoading?: boolean;
  defaultValues?: Partial<PromoDisplayCheckFormData>;
};

const defaultFormValues: PromoDisplayCheckFormData = {
  offerId: '',
  promoDisplayConfigId: '',
  brand: 'luxuryescapes',
  sessionId: '',
};

export default function PromoDisplayCheckForm({
  onSubmit,
  isLoading: externalLoading = false,
  defaultValues = {},
}: PromoDisplayCheckFormProps) {
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();

  // Read URL parameters and merge with defaultValues
  const getUrlParams = useCallback(() => {
    const urlOfferId = query.get('offerId') || '';
    const urlPromoDisplayConfigId = query.get('promoDisplayConfigId') || '';
    const urlBrand = query.get('brand') || defaultFormValues.brand;
    const urlSessionId = query.get('sessionId') || '';

    return {
      offerId: urlOfferId,
      promoDisplayConfigId: urlPromoDisplayConfigId,
      brand: urlBrand,
      sessionId: urlSessionId,
    };
  }, [query]);

  // Initial values: URL params take precedence, then defaultValues prop, then defaultFormValues
  const initialValues = useMemo(() => {
    const urlParams = getUrlParams();
    return {
      ...defaultFormValues,
      ...defaultValues,
      ...urlParams,
    };
  }, [defaultValues, getUrlParams]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PromoDisplayCheckFormData>({
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  const { checkPromoDisplay, isChecking, data, error } = usePromoDisplayCheck();

  const isLoading = externalLoading || isChecking;

  const handleFormSubmit = (formData: PromoDisplayCheckFormData) => {
    // Update URL with form values
    const urlParams: Record<string, string> = {};
    if (formData.offerId) urlParams.offerId = formData.offerId;
    if (formData.promoDisplayConfigId) urlParams.promoDisplayConfigId = formData.promoDisplayConfigId;
    if (formData.brand) urlParams.brand = formData.brand;
    if (formData.sessionId) urlParams.sessionId = formData.sessionId;

    const newLocation = addQuery(location, urlParams);
    history.push(newLocation);

    const params: CheckPromoDisplayParams = {
      offerId: formData.offerId,
      promoDisplayConfigId: formData.promoDisplayConfigId,
      brand: formData.brand,
      ...(formData.sessionId && { sessionId: formData.sessionId }),
    };

    checkPromoDisplay(params);

    // Call the original onSubmit handler if provided
    if (onSubmit) {
      onSubmit(formData);
    }
  };

  const result = data?.result;

  return (
    <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={12} md={8} lg={6}>
          <Stack spacing={2}>
            <TextField
              {...register('offerId', { required: 'Offer ID is required' })}
              label="Offer ID"
              variant="outlined"
              fullWidth
              required
              error={!!errors.offerId}
              helperText={errors.offerId?.message}
            />

            <TextField
              {...register('promoDisplayConfigId', { required: 'Promo Display Config ID is required' })}
              label="Promo Display Config ID"
              variant="outlined"
              fullWidth
              required
              error={!!errors.promoDisplayConfigId}
              helperText={errors.promoDisplayConfigId?.message}
            />

            <TextField
              {...register('brand', { required: 'Brand is required' })}
              label="Brand"
              variant="outlined"
              fullWidth
              select
              required
              error={!!errors.brand}
              helperText={errors.brand?.message}
            >
              {brands.map((brand) => (
                <MenuItem key={brand.value} value={brand.value}>
                  {brand.title}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              {...register('sessionId')}
              label="Session ID"
              variant="outlined"
              required
              fullWidth
              error={!!errors.sessionId}
              helperText={errors.sessionId?.message || 'Optional session ID for tracking'}
            />

            <Button type="submit" variant="contained" color="primary" disabled={isLoading} fullWidth>
              {isLoading ? 'Checking...' : 'Check Promo Display'}
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/* Results Section */}
      {(result || error) && (
        <Grid container spacing={2} justifyContent="center" sx={{ mt: 3 }}>
          <Grid item xs={12} md={8} lg={6}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Error: {error.message}
              </Alert>
            )}

            {result && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Check Results
                  </Typography>

                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Check Passed:
                      </Typography>
                      <Typography
                        variant="body1"
                        color={result.checkPassed ? 'success.main' : 'error.main'}
                        fontWeight="bold"
                      >
                        {result.checkPassed ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                    {result.resultCode && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Result Code:
                        </Typography>
                        <Typography variant="body1">{result.resultCode}</Typography>
                      </Box>
                    )}

                    {result.codeName && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Code Name:
                        </Typography>
                        <Typography variant="body1">{result.codeName}</Typography>
                      </Box>
                    )}

                    {result.promoDisplayOptionId && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Promo Display Option ID:
                        </Typography>
                        <Typography variant="body1">{result.promoDisplayOptionId}</Typography>
                      </Box>
                    )}

                    {result.displayOptionKey && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Display Option Key:
                        </Typography>
                        <Typography variant="body1">{result.displayOptionKey}</Typography>
                      </Box>
                    )}

                    {data?.message && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Message:
                        </Typography>
                        <Typography variant="body1">{data.message}</Typography>
                      </Box>
                    )}
                    <DebugModal type="generic" data={result} />
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
